/**
 * ============================================================================
 * NewAuditPlanDialog — modal for creating a new annual audit plan (Phase 7).
 * Owner: Phase 7 — Internal Audit Programme Module.
 *
 * Posts to the PG-backed POST /api/v1/audit-plans (drafts/61 F0-8), which inserts
 * a row into internal_audit.audit_plans at status DRAFT. On success the plan list
 * refetches. ICH Q10 §II.F: the annual plan is the parent of scheduled audits.
 *
 * URS rows: INA-001 (annual plan).
 * ============================================================================ */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@edms/design-system';
import { buildHeaders, apiUrl } from '../../api/client';
import styles from './AuditCalendar.module.css';

export interface NewAuditPlanDialogProps {
  onClose: () => void;
  onCreated: (planId: string) => void;
}

export function NewAuditPlanDialog({ onClose, onCreated }: NewAuditPlanDialogProps) {
  const { t } = useTranslation();
  const thisYear = new Date().getUTCFullYear();
  const [planYear, setPlanYear] = useState<number>(thisYear);
  const [title, setTitle] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string>();

  async function submit() {
    setSubmitting(true);
    setErrorMsg(undefined);
    try {
      const r = await fetch(apiUrl('/api/v1/audit-plans'), {
        method: 'POST',
        headers: buildHeaders({ json: true }),
        body: JSON.stringify({
          planYear,
          title: title.trim() || `${planYear} Internal Audit Plan`,
        }),
      });
      if (!r.ok) {
        const body = await r.json().catch(() => ({}));
        setErrorMsg(
          (body as { detail?: string }).detail ??
            t('internalAudit.plan.create.failed', { defaultValue: 'Create plan failed.' }),
        );
        return;
      }
      const body = (await r.json()) as { id?: string; planId?: string };
      onCreated(body.planId ?? body.id ?? '');
    } catch (e) {
      setErrorMsg(String(e));
    } finally {
      setSubmitting(false);
    }
  }

  const yearRange = [thisYear - 1, thisYear, thisYear + 1, thisYear + 2];

  return (
    <div className={styles.dialogShade} role="dialog" aria-modal="true">
      <div className={styles.dialogBox}>
        <h2 className={styles.dialogTitle}>
          {t('internalAudit.plan.create.title', { defaultValue: 'New annual audit plan' })}
        </h2>
        <p className={styles.dialogSubtitle}>
          {t('internalAudit.plan.create.subtitle', {
            defaultValue:
              'Opens a DRAFT plan for the chosen year. Schedule audits against it once approved (ICH Q10 §II.F).',
          })}
        </p>

        <div className={styles.formField}>
          <label htmlFor="plan-year">
            {t('internalAudit.plan.create.yearLabel', { defaultValue: 'Plan year' })}
          </label>
          <select
            id="plan-year"
            value={planYear}
            onChange={(e) => setPlanYear(Number(e.target.value))}
          >
            {yearRange.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
        </div>

        <div className={styles.formField}>
          <label htmlFor="plan-title">
            {t('internalAudit.plan.create.titleLabel', { defaultValue: 'Title (optional)' })}
          </label>
          <input
            id="plan-title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={`${planYear} Internal Audit Plan`}
          />
        </div>

        {errorMsg && (
          <div className={styles.dialogError} role="alert">
            {errorMsg}
          </div>
        )}

        <div className={styles.dialogActions}>
          <Button variant="secondary" onClick={onClose}>
            {t('common.cancel', { defaultValue: 'Cancel' })}
          </Button>
          <Button variant="primary" onClick={submit} disabled={submitting}>
            {submitting
              ? t('common.submitting', { defaultValue: 'Submitting…' })
              : t('internalAudit.plan.create.submit', { defaultValue: 'Create plan' })}
          </Button>
        </div>
      </div>
    </div>
  );
}
